import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

interface AppSplashContextValue {
  /** True when the splash overlay should be visible. */
  visible: boolean;
  /**
   * Show the splash and arm a safety timeout. The splash will hide on the
   * sooner of (a) explicit hide(), (b) the timeout. Default safety: 4000ms.
   */
  show: (safetyMs?: number) => void;
  /** Hide the splash immediately (data is ready). */
  hide: () => void;
}

const noop = () => {};

const AppSplashContext = createContext<AppSplashContextValue>({
  visible: false,
  show: noop,
  hide: noop,
});

const DEFAULT_SAFETY_MS = 4000;

/**
 * Wraps the app so any component can show the branded splash during a
 * transition (login → home, deep navigation, anything that would expose
 * the body to a flash of white).
 *
 * Pattern:
 *   const splash = useAppSplash();
 *   splash.show();              // arm + show, auto-hide after 4s
 *   await doSlowWork();
 *   splash.hide();              // dismiss when ready
 *
 * The safety timeout matters: forcing the splash to depend solely on
 * downstream `hide()` calls means a buggy/slow consumer can leave the
 * user staring at a logo forever. 4s is the longest acceptable wait we
 * accept before showing the page even if its data isn't fully ready.
 */
export const AppSplashProvider = ({ children }: { children: React.ReactNode }) => {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const hide = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
  }, []);

  const show = useCallback(
    (safetyMs: number = DEFAULT_SAFETY_MS) => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
      setVisible(true);
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        setVisible(false);
      }, safetyMs);
    },
    [],
  );

  const value = useMemo(() => ({ visible, show, hide }), [visible, show, hide]);

  return <AppSplashContext.Provider value={value}>{children}</AppSplashContext.Provider>;
};

export const useAppSplash = () => useContext(AppSplashContext);
